import { Component, forwardRef, Input, Type, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseUpsertComponent } from '@cl/core/components';
import { BaseService } from '@cl/core/services';
import { ServiceFactory } from '@cl/core/services/service.factory';
import { BaseModel, CommandResult, EntityType } from '@cl/shared/models';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

@Component({
    selector: 'cl-search-field-form-control',
    standalone: false,
    templateUrl: './search-field-form-control.component.html',
    styleUrl: './search-field-form-control.component.less',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchFieldFormControlComponent),
            multi: true,
        },
    ],
})
export class SearchFieldFormControlComponent implements ControlValueAccessor {
    @ViewChild('valueSelect') valueSelect: NzSelectComponent;

    @Input() fieldLabel: string;

    @Input() fieldPlaceholder: string;

    @Input() allowClear: boolean = false;

    @Input() modelType: EntityType;

    @Input() customSearchFn?: (searchText: string) => Promise<CommandResult<BaseModel[]>>;

    @Input() customToStringFn?: (object: BaseModel) => string;

    @Input() minimumSearchTextLengthThreshold: number = 3;

    @Input() objectMode: boolean = false;

    @Input() createMode: boolean = false;

    @Input() editAllowed: boolean = false;
    @Input() editPermissions: string[] = [];

    @Input() upsertComponent: Type<BaseUpsertComponent<BaseModel, BaseService<BaseModel>>>;

    isSearchPerformed: boolean;

    listOfOption: Array<{ value: BaseModel; text: string }> = [];
    searchSubject: Subject<string> = new Subject<string>();

    nzFilterOption = () => true;
    service: BaseService<BaseModel>;

    value: number | BaseModel;
    onChange = (_: number | BaseModel): void => {};
    onTouched = (): void => {};

    isDisabled: boolean = false;

    constructor(
        private serviceFactory: ServiceFactory,
        private messageService: NzMessageService,
        private modalService: NzModalService,
    ) {
        this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe(async (searchText: string): Promise<void> => {
            try {
                const foundOptions: BaseModel[] = this.customSearchFn
                    ? (await this.customSearchFn(searchText)).data
                    : (await this.service.search(searchText).toPromise()).data;
                this.listOfOption = [];
                foundOptions.forEach((d) => {
                    this.listOfOption.push({
                        value: d,
                        text: this.customToStringFn ? this.customToStringFn(d) : this.service.toString(d),
                    });
                });
                this.isSearchPerformed = true;

                // set input to be in focus again, because afther isSearchPeformed is set to true,
                // suffixActions takes over the focus, so it has to be set back for uninterupted search text inputting
                setTimeout(() => {
                    requestAnimationFrame(() => {
                        this.valueSelect.focus();
                    });
                });
            } catch (ex) {
                console.error(ex);
                if (ex.error?.errors) {
                    ex.error.errors.forEach((error) => {
                        this.messageService.error(error);
                    });
                    return;
                }

                this.messageService.error('Kļūda atlasot izvēles opcijas');
                return;
            }
        });
    }

    ngOnInit(): void {
        this.service = this.serviceFactory.getService(this.modelType);
    }

    handleChange(selectedValue: number | BaseModel): void {
        if (this.value == selectedValue) return;

        this.value = selectedValue;
        this.onChange(selectedValue);
    }

    async writeValue(value: number | BaseModel): Promise<void> {
        if (this.value == value) return;

        this.value = value;

        if (!value) return;

        const valueId: number = this.objectMode ? (value as BaseModel).id : (value as number);

        const existing = this.listOfOption.find((opt) => opt.value.id === valueId);

        if (existing) return;
        (async (): Promise<void> => {
            try {
                const result: CommandResult<BaseModel> = await this.service.getSingle(valueId);
                if (result.data) {
                    const entity = result.data;

                    this.listOfOption = [
                        {
                            value: entity,
                            text: this.customToStringFn ? this.customToStringFn(entity) : this.service.toString(entity),
                        },
                        ...this.listOfOption,
                    ];
                    const existing = this.listOfOption.find((opt) => opt.value.id === valueId);
                    this.value = this.objectMode ? existing.value : existing.value.id;
                } else {
                    console.warn(`${this.modelType} with ID ${valueId} not found`);
                }
            } catch (err) {
                console.error('Error fetching entity in writeValue:', err);
            }
        })();
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    async search(text: string): Promise<void> {
        if (text.length < this.minimumSearchTextLengthThreshold) {
            return;
        }

        this.searchSubject.next(text);
    }

    async addObject(): Promise<void> {
        if (!this.createMode) {
            return;
        }
        const modal: NzModalRef<BaseUpsertComponent<BaseModel, BaseService<BaseModel>>> = this.modalService.create({
            nzTitle: 'Pievienošana',
            nzContent: this.upsertComponent,
        });

        modal.afterOpen.subscribe(() => {
            const component: BaseUpsertComponent<BaseModel, BaseService<BaseModel>> = modal.getContentComponent();
            component.createAndReturn = true;
            component.fromSearchField = true;
            component.initForm();
            component.afterInit();
            component.objectForm.markAsDirtyAndUpdateAll();
        });

        modal.afterClose.subscribe(async (obj: BaseModel): Promise<void> => {
            if (!obj) return;

            this.listOfOption = [
                {
                    value: obj,
                    text: this.customToStringFn ? this.customToStringFn(obj) : this.service.toString(obj),
                },
                ...this.listOfOption,
            ];

            this.handleChange(this.objectMode ? obj : obj.id);
        });
    }

    async editObject(): Promise<void> {
        if (!this.editAllowed) {
            return;
        }

        const modal: NzModalRef<BaseUpsertComponent<BaseModel, BaseService<BaseModel>>> = this.modalService.create({
            nzTitle: 'Labošana',
            nzContent: this.upsertComponent,
        });

        const valueId: number = this.objectMode ? (this.value as BaseModel).id : (this.value as number);

        const fullObjectData: BaseModel = (await this.service.getSingle(valueId)).data;

        modal.afterOpen.subscribe((): void => {
            const component: BaseUpsertComponent<BaseModel, BaseService<BaseModel>> = modal.getContentComponent();
            component.setEditMode(fullObjectData);
            component.returnObject = true;
            component.objectForm.markAsDirtyAndUpdateAll();
        });

        modal.afterClose.subscribe(async (obj: BaseModel): Promise<void> => {
            if (!obj) return;

            this.listOfOption = [
                {
                    value: obj,
                    text: this.customToStringFn ? this.customToStringFn(obj) : this.service.toString(obj),
                },
                ...this.listOfOption,
            ];

            this.handleChange(this.objectMode ? obj : obj.id);
        });
    }

    async clearSelection(): Promise<void> {
        if (!this.createMode || !this.allowClear) {
            return;
        }

        await this.writeValue(null);
    }

    clearOptions(): void {
        this.listOfOption = [];
        this.isSearchPerformed = false;
    }
}
